// SmokeBar — bottom-of-map time scrubber for the HRRR-Smoke layer.
//
// Shows when the `hrrr-smoke` layer is on. Lets the pilot drag through
// the published forecast hours (Phase 1: 0, 3, 6, 12, 18) and the
// layer's image source swaps to the matching pre-rendered PNG. The
// underlying ingest cron at .github/workflows/smoke-ingest.yml runs
// every hour at HH:35 UTC and re-publishes the full forecast set.
//
// Stacking: this bar appears whenever the smoke layer is visible and
// stacks alongside the other bottom bars (WxHazard / NEXRAD /
// SfcAnalysis / TerrainAdvisor). Same arithmetic as SfcAnalysisBar.

import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useInstrumentPrefsStore } from '../stores/instrumentPrefsStore';
import { useInstrumentStripHeight } from './useInstrumentStripHeight';
import { pickClosestLevel } from './layers/hrrrSmoke';

// Phase 1 forecast hour set. Keep in sync with PHASE_1_HOURS in
// scripts/smoke-ingest/smoke_ingest.py (FORECAST_HOURS) and with
// the layer module's pickForecastHour() in src/map/layers/hrrrSmoke.ts.
const SMOKE_HOURS = [0, 3, 6, 12, 18] as const;
const SLIDER_MIN = 0;
const SLIDER_MAX = SMOKE_HOURS.length - 1;

export default function SmokeBar() {
  const showSmoke = useLayerVisibilityStore((s) => s.layers['hrrr-smoke']);
  const showSfcAnalysis = useLayerVisibilityStore((s) => s.layers.sfcanalysis);
  const showWxHazards = useLayerVisibilityStore((s) => s.layers.wxhazards);
  const showNexrad = useLayerVisibilityStore((s) => s.layers.nexrad);
  const showInstruments = useInstrumentPrefsStore((s) => s.showInstruments);
  const smokeOffsetH = useUiStore((s) => s.smokeOffsetH);
  const setSmokeOffsetH = useUiStore((s) => s.setSmokeOffsetH);
  const altitudeSliderFt = useUiStore((s) => s.altitudeSliderFt);

  const instrumentH = useInstrumentStripHeight(showSmoke);

  if (!showSmoke) return null;

  // Match SfcAnalysisBar's stacking arithmetic. Smoke sits ABOVE
  // SfcAnalysis (taller weather context). Same constants in case
  // either bar's footprint shifts.
  const INSTRUMENT_BOTTOM_OFFSET = 12;
  const ABOVE_INSTRUMENTS_GAP_PX = 14;
  const HAZARD_BAR_FOOTPRINT_PX = 110;
  const NEXRAD_BAR_FOOTPRINT_PX = 70;
  const SFC_BAR_FOOTPRINT_PX = 70;
  const baseBottomPx = showInstruments
    ? INSTRUMENT_BOTTOM_OFFSET + instrumentH + ABOVE_INSTRUMENTS_GAP_PX
    : 14;
  const bottomPx = baseBottomPx
    + (showWxHazards ? HAZARD_BAR_FOOTPRINT_PX : 0)
    + (showNexrad ? NEXRAD_BAR_FOOTPRINT_PX : 0)
    + (showSfcAnalysis ? SFC_BAR_FOOTPRINT_PX : 0);

  // Slider index ↔ smoke hour. Snap to the closest known step.
  const currentIdx = Math.max(0, SMOKE_HOURS.indexOf(smokeOffsetH as 0 | 3 | 6 | 12 | 18));
  const onChange = (idx: number) => {
    const clamped = Math.max(0, Math.min(SLIDER_MAX, idx));
    setSmokeOffsetH(SMOKE_HOURS[clamped]);
  };

  return (
    <div
      className="absolute left-0 right-0 z-10 flex justify-center pointer-events-none"
      style={{ bottom: `${bottomPx}px` }}
    >
      <div className="pointer-events-auto bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-bar px-3 py-2 w-[min(28rem,calc(100vw-1.5rem))]">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-start min-w-[5rem]">
            <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Smoke</span>
            <span className="font-mono text-[12px] text-ember-300 leading-none mt-0.5">
              {labelFor(smokeOffsetH)}
            </span>
          </div>
          <div className="flex-1">
            <input
              type="range"
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={1}
              value={Math.max(0, currentIdx)}
              onChange={(e) => onChange(parseInt(e.currentTarget.value, 10))}
              aria-label="HRRR-Smoke forecast offset"
              className="w-full accent-ember-500"
            />
            <div className="relative h-3 mt-0.5 text-[9px] font-mono text-cockpit-400">
              <span className="absolute" style={{ left: '0%' }}>now</span>
              <span className="absolute -translate-x-1/2" style={{ left: '50%' }}>+6h</span>
              <span className="absolute -translate-x-full" style={{ left: '100%' }}>+18h</span>
            </div>
          </div>
          <div className="text-right min-w-[4rem]">
            <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Valid</span>
            <div className="font-mono text-[11px] text-cockpit-100 leading-none mt-0.5">
              {wallClockAt(smokeOffsetH)}
            </div>
            <span className="text-[9px] tracking-widest text-cockpit-400 uppercase mt-0.5 block">Alt</span>
            <div className="font-mono text-[10px] text-ember-200 leading-none mt-0.5">
              ~{Math.round(altitudeAGLFor(altitudeSliderFt) / 100) * 100}′
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function labelFor(h: number): string {
  if (h === 0) return 'now';
  return `+${h}h`;
}

// Approximate AGL altitude for the closest published hybrid level
// (matches LEVEL_ALT_AGL_FT in scripts/smoke-ingest/smoke_ingest.py).
const LEVEL_ALT_AGL: Record<number, number> = {
  0:    30,
  1:   100,
  5:  1000,
  10: 3300,
  15: 6200,
  20: 9500,
  25: 13800,
};
function altitudeAGLFor(altFt: number): number {
  const level = pickClosestLevel(altFt);
  return LEVEL_ALT_AGL[level] ?? 0;
}

function wallClockAt(h: number): string {
  const d = new Date(Date.now() + h * 3600_000);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}Z`;
}
